import { format, utcToZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '../../../app/AuthorizationProvider';
import { ModalConfirmation } from '../../../components/ModalConfirmation';
import { Agent, RequestType, ScheduleInfo } from '../../../graphql/components';
import { PATH_REQUESTS, PATH_SCHEDULING_STUDIO_EDIT } from '../../../router/routes';
import { fullNameOfAgent } from '../../../utils/helpers';
import { REQUEST_URL_KEYS } from '../../Requests/RequestsScene';
import { PTORequestPartial, ProductionChartScheduleElement } from '../types';

interface ElementsInCollisionModalProps {
  agents: Record<string, Agent>;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  productionSchedulesInConflict: ScheduleInfo[];
  ptoRequestsInConflict: PTORequestPartial[];
}

export type ElementsInCollisionModalState =
  | {
      state: 'open';
      customSubmitHandler?: () => void;
      onSubmitSuccess?: () => void;
      onSubmitError?: () => void;
      ptoRequestsInConflict: PTORequestPartial[];
      productionElementsInConflict: ProductionChartScheduleElement[];
      productionSchedulesInConflict: ScheduleInfo[];
    }
  | {
      state: 'loading';
    }
  | {
      state: 'resolved';
    };

export const ElementsInCollisionModal: React.FC<ElementsInCollisionModalProps> = ({
  agents,
  isOpen,
  onClose,
  onSubmit,
  productionSchedulesInConflict,
  ptoRequestsInConflict,
}) => {
  const { t } = useTranslation();
  const { timezone } = useAuth();

  if (!isOpen) {
    return null;
  }

  const maximumOfItemsInConflict = Math.max(productionSchedulesInConflict.length, ptoRequestsInConflict.length);

  return (
    <ModalConfirmation
      headerText={t('layout.warning')}
      bodyText={
        <div style={{ whiteSpace: 'pre-line' }}>
          <div className="mb-4">
            {t('schedule.modal.collisionWithElementsBody', {
              count: maximumOfItemsInConflict,
            })}
            .
          </div>
          {!!productionSchedulesInConflict.length && (
            <>
              <span>
                {t('schedule.modal.collisionWithElementsBody2', { count: productionSchedulesInConflict.length })}
              </span>
              <ul className="mb-4">
                {productionSchedulesInConflict.map(({ id, name }) => (
                  <li key={id}>
                    <Link
                      data-test-id="link-to-schedule-in-conflict"
                      target="_blank"
                      to={`${PATH_SCHEDULING_STUDIO_EDIT}/${id}`}
                    >
                      {name}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {!!ptoRequestsInConflict.length && (
            <>
              <span>{t('schedule.modal.collisionWithElementsBody4', { count: ptoRequestsInConflict.length })}</span>
              <ul className="mb-4">
                {ptoRequestsInConflict.map(({ id, agentId, endDateTime, startDateTime }) => (
                  <li key={id}>
                    <Link
                      data-test-id="link-to-absence-request-in-conflict"
                      target="_blank"
                      to={`${PATH_REQUESTS}/${REQUEST_URL_KEYS[RequestType.Pto]}/${id}`}
                    >
                      {fullNameOfAgent(agents[agentId])}:{' '}
                      {format(utcToZonedTime(startDateTime, timezone), t('date.formats.full'))} -{' '}
                      {format(utcToZonedTime(endDateTime, timezone), t('date.formats.full'))}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          <span>{t('schedule.modal.collisionWithElementsBody3')}</span>
        </div>
      }
      submitText={t('layout.action.proceed')}
      closeText={t('layout.action.cancel')}
      onSubmit={onSubmit}
      show
      onClose={onClose}
    />
  );
};
